import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toastError, toastSuccess } from '../lib/toast.js';
import { findSelectMenu, type SelectMenu } from '../lib/select-menu.js';

/**
 * The label a category shows on the page, rather than its key.
 *
 * The warning names categories, and "moderation" is not what the row says in
 * Japanese. Read off the row that is already rendered instead of shipping a
 * tenth copy of the nine names into the client bundle.
 */
function categoryLabel(form: HTMLFormElement, category: string): string {
  const row = $<HTMLElement>(`[data-log-rule="${CSS.escape(category)}"]`, form);
  return row?.dataset.logLabel?.trim() || category;
}

export function initLogsPage(): void {
  const form = $<HTMLFormElement>('[data-logs-form]');
  if (!form) return;

  const guildId = form.dataset.guildId ?? '';

  /*
   * Saving is armed before the widgets: saving is the essential function of the
   * page, it must not depend on a picker mounting nor on reading the data
   * block, both of which can fail.
   */
  trackDirtyForm({
    form,
    successMessage: ct('logsSaved', 'Logging saved.'),
    onSave: async () => {
      const rules = $$<HTMLElement>('[data-log-rule]', form).map((rule) => ({
        category: rule.dataset.logRule ?? '',
        enabled: rule.querySelector<HTMLInputElement>('[data-field="enabled"]')?.checked ?? false,
        channelId: channelMenu(rule)?.single ?? '',
      }));

      const answer = await api.put<{ incomplete?: string[] }>(`/api/logs?guildId=${guildId}`, {
        guildId,
        rules,
        ignoredChannelIds: findSelectMenu(form, 'ignoredChannelIds')?.value ?? [],
        ignoredRoleIds: findSelectMenu(form, 'ignoredRoleIds')?.value ?? [],
        ignoreBots: $<HTMLInputElement>('[data-field="ignoreBots"]', form)?.checked ?? true,
      });

      /*
       * Reported after the save, never instead of it.
       *
       * The switch stays where the staff put it — the handler used to turn it
       * off instead, which read as a page that would not save. The categories
       * are named because "three are incomplete" sends somebody hunting through
       * nine rows.
       */
      const incomplete = answer.incomplete ?? [];
      if (incomplete.length > 0) {
        toastError(
          ct('logsCategoryNoChannel', 'Saved. These categories have no channel yet: {list}', {
            list: incomplete.map((key) => categoryLabel(form, key)).join(', '),
          }),
        );
      }
    },
  });

  /**
   * The "everything into one channel" shortcut.
   *
   * Configuring nine categories one by one is discouraging: most servers want a
   * single log channel to begin with, and will refine later.
   */
  /** Channel-picker component of a rule. */
  const channelMenu = (rule: HTMLElement): SelectMenu | null => findSelectMenu(rule, 'channelId');

  $('[data-logs-preset]')?.addEventListener('click', () => {
    const rules = $$<HTMLElement>('[data-log-rule]');
    const channelId = rules[0] ? (channelMenu(rules[0])?.single ?? '') : '';

    if (!channelId) {
      // With no reference channel, we highlight the first dropdown rather than
      // doing nothing without explanation.
      rules[0]?.querySelector<HTMLElement>('button')?.focus();
      return;
    }

    for (const rule of rules) {
      const menu = channelMenu(rule);
      if (menu) menu.value = [channelId];

      const toggle = rule.querySelector<HTMLInputElement>('[data-field="enabled"]');
      if (toggle) toggle.checked = true;
    }

    form.dispatchEvent(new Event('change', { bubbles: true }));
    toastSuccess(ct('allSameChannel', 'All categories now point to the same channel.'));
  });
}
